import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: Array<'customer' | 'restaurant_owner' | 'admin'>;
  requireAuth?: boolean;
}

export function ProtectedRoute({ 
  children, 
  allowedRoles = ['customer', 'restaurant_owner', 'admin'],
  requireAuth = true 
}: ProtectedRouteProps) {
  const { user, userRole, loading, roleLoading } = useAuth();
  const location = useLocation();
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Timeout de loading para evitar loading infinito
  useEffect(() => {
    if (loading || (user && userRole === null && roleLoading)) {
      const timeout = setTimeout(() => {
        console.warn('Loading timeout reached');
        setLoadingTimeout(true);
      }, 5000); // 5s timeout
      
      return () => clearTimeout(timeout);
    } else {
      setLoadingTimeout(false);
    }
  }, [loading, user, userRole, roleLoading]);

  // Se timeout atingido e ainda sem userRole, redirecionar para auth
  if (loadingTimeout && user && !userRole) {
    console.warn('Timeout reached, redirecting to auth');
    return <Navigate to="/auth" replace />;
  }

  // Show loading while auth is loading OR while role is being fetched for authenticated users
  if (loading || (user && userRole === null && roleLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-6">
          <CardContent className="flex flex-col items-center space-y-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <div className="text-center">
              <span className="text-sm font-medium">Carregando perfil...</span>
              {roleLoading && (
                <p className="text-xs text-muted-foreground mt-1">
                  Validando permissões
                </p>
              )}
              {loadingTimeout && (
                <p className="text-xs text-destructive mt-1">Redirecionando...</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Redirect to auth if authentication is required but user is not authenticated
  if (requireAuth && !user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Smart redirection from main page - redirect authenticated users to their dashboard
  if (user && userRole && location.pathname === '/') {
    switch (userRole) {
      case 'restaurant_owner':
        return <Navigate to="/restaurant" replace />;
      case 'admin':
        return <Navigate to="/admin" replace />;
      default:
        // Customers can stay on main page
        break;
    }
  }

  // Check role permissions
  if (requireAuth && userRole && !allowedRoles.includes(userRole)) {
    // Redirect based on user role
    switch (userRole) {
      case 'restaurant_owner':
        return <Navigate to="/restaurant" replace />;
      case 'admin':
        return <Navigate to="/admin" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}